import { signal } from "@preact-signals/safe-react";
import config from "@src/config";
import { buildAuthorizationParams } from "@src/utils/auth-params";
import {
	captureReferralCode,
	getStoredReferralCode,
} from "@src/utils/referral-tracker";
import { captureUtmParams, getStoredUtmParams } from "@src/utils/utm-tracker";
import { auth0Client } from "./auth0-provider-with-navigate";
import {
	BROWSER_LOGIN_CANCELLED_ERROR,
	auth0SpaTransactionStorageKey,
} from "./desktop-browser-login-ipc";

export type DesktopBrowserLoginStatus =
	| "idle"
	| "waiting"
	| "error"
	| "cancelled";

export type DesktopBrowserLoginState = {
	status: DesktopBrowserLoginStatus;
	error?: string;
	/** Options used for the last attempt — enables Retry from the overlay. */
	lastOptions?: InteractiveLoginOptions;
};

export type InteractiveLoginOptions = {
	appState?: {
		returnTo?: string;
		utmParams?: unknown;
	};
	authorizationParams?: Record<string, string>;
};

export const desktopBrowserLoginSignal = signal<DesktopBrowserLoginState>({
	status: "idle",
});

let inFlight: Promise<void> | null = null;
let callbackWaitAbort: (() => void) | null = null;

export function shouldUseDesktopBrowserLogin(): boolean {
	return Boolean(
		typeof window !== "undefined" &&
			window.electronAPI?.isElectron &&
			config.features.desktopBrowserLogin,
	);
}

function clearAuth0Transaction() {
	try {
		localStorage.removeItem(
			auth0SpaTransactionStorageKey(config.auth.clientId),
		);
	} catch {
		// localStorage may be unavailable
	}
}

async function shouldPromptAccountPicker(): Promise<boolean> {
	const api = window.electronAPI;
	if (!api?.getProfilesState) return false;
	try {
		const state = await api.getProfilesState();
		return state.profiles.length > 1;
	} catch {
		return false;
	}
}

function waitForBrowserLoginCallback(): Promise<string> {
	const api = window.electronAPI;
	if (!api?.onBrowserLoginCallback) {
		return Promise.reject(new Error("Browser login IPC is unavailable"));
	}

	return new Promise((resolve, reject) => {
		const unsubscribe = api.onBrowserLoginCallback((payload) => {
			cleanup();
			if (payload.ok) {
				resolve(payload.url);
			} else if (payload.error === BROWSER_LOGIN_CANCELLED_ERROR) {
				reject(new DOMException(BROWSER_LOGIN_CANCELLED_ERROR, "AbortError"));
			} else {
				reject(new Error(payload.error));
			}
		});

		const cleanup = () => {
			unsubscribe();
			callbackWaitAbort = null;
		};

		callbackWaitAbort = () => {
			cleanup();
			reject(new DOMException(BROWSER_LOGIN_CANCELLED_ERROR, "AbortError"));
		};
	});
}

/**
 * Interactive login entry point for both web and desktop.
 * On desktop (flag on): system browser + loopback PKCE via Auth0 SPA SDK.
 * Otherwise: standard in-window `loginWithRedirect`.
 */
export async function loginInteractively(
	options: InteractiveLoginOptions = {},
): Promise<void> {
	if (!shouldUseDesktopBrowserLogin()) {
		await auth0Client.loginWithRedirect({
			appState: options.appState,
			authorizationParams: options.authorizationParams,
		});
		return;
	}

	if (inFlight) {
		return inFlight;
	}

	inFlight = runDesktopBrowserLogin(options).finally(() => {
		inFlight = null;
	});
	return inFlight;
}

async function runDesktopBrowserLogin(
	options: InteractiveLoginOptions,
): Promise<void> {
	const api = window.electronAPI;
	if (
		!api?.startBrowserLogin ||
		!api.openExternal ||
		!api.completeBrowserLogin
	) {
		throw new Error("Desktop browser login APIs are unavailable");
	}

	desktopBrowserLoginSignal.value = {
		status: "waiting",
		lastOptions: options,
	};

	try {
		const { port, redirectUri: customRedirectUri } =
			await api.startBrowserLogin();
		const redirectUri =
			customRedirectUri ?? `http://127.0.0.1:${port}/callback`;

		const utmParams = captureUtmParams() ?? getStoredUtmParams();
		const referralCode = captureReferralCode() ?? getStoredReferralCode();
		const baseParams = buildAuthorizationParams(utmParams, referralCode);
		const promptAccountPicker = await shouldPromptAccountPicker();

		// Always set prompt + max_age so the system browser's Auth0 SSO session
		// cannot silently complete login after logout.
		const authorizationParams: Record<string, string> = {
			audience: config.auth.audience,
			...baseParams,
			...options.authorizationParams,
			redirect_uri: redirectUri,
			prompt: promptAccountPicker ? "select_account" : "login",
			max_age: "0",
		};

		const callbackPromise = waitForBrowserLoginCallback();

		await auth0Client.loginWithRedirect({
			appState: options.appState,
			authorizationParams,
			openUrl: async (url) => {
				await api.openExternal(url);
			},
		});

		const callbackUrl = await callbackPromise;
		await auth0Client.handleRedirectCallback(callbackUrl);
		desktopBrowserLoginSignal.value = { status: "idle" };
		await api.completeBrowserLogin();
	} catch (error) {
		const cancelled =
			(error instanceof DOMException && error.name === "AbortError") ||
			(error instanceof Error &&
				error.message === BROWSER_LOGIN_CANCELLED_ERROR);

		if (cancelled) {
			clearAuth0Transaction();
			desktopBrowserLoginSignal.value = {
				status: "cancelled",
				lastOptions: options,
			};
			return;
		}

		clearAuth0Transaction();
		await api.cancelBrowserLogin?.().catch(() => undefined);
		const message =
			error instanceof Error ? error.message : "Browser login failed";
		desktopBrowserLoginSignal.value = {
			status: "error",
			error: message,
			lastOptions: options,
		};
		throw error;
	}
}

export async function cancelDesktopBrowserLogin(): Promise<void> {
	callbackWaitAbort?.();
	callbackWaitAbort = null;
	clearAuth0Transaction();
	try {
		await window.electronAPI?.cancelBrowserLogin?.();
	} catch {
		// ignore
	}
	desktopBrowserLoginSignal.value = {
		status: "cancelled",
		lastOptions: desktopBrowserLoginSignal.value.lastOptions,
	};
}

export async function retryDesktopBrowserLogin(): Promise<void> {
	const options = desktopBrowserLoginSignal.value.lastOptions ?? {};
	await loginInteractively(options);
}

export function dismissDesktopBrowserLoginOverlay(): void {
	desktopBrowserLoginSignal.value = { status: "idle" };
}
